"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import type { Dictionary } from "@/i18n/dictionaries";
import { localeNames, locales, type Locale } from "@/i18n/config";
import { BrandLogo } from "./brand-logo";

interface SiteHeaderProps {
  locale: Locale;
  dict: Dictionary;
}

export function SiteHeader({ locale, dict }: SiteHeaderProps) {
  const pathname = usePathname() || `/${locale}`;
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const restPath = pathname.split("/").slice(2).join("/");
  const localeHref = (target: Locale) =>
    `/${target}${restPath ? `/${restPath}` : ""}`;

  const navItems = [
    { href: `/${locale}/service`, label: dict.nav.service },
    { href: `/${locale}/experiences`, label: dict.nav.experiences },
    { href: `/${locale}/#studio`, label: dict.nav.studio },
    { href: `/${locale}/lookup`, label: dict.nav.lookup },
  ];

  const isActive = (href: string) =>
    href === `/${locale}/service`
      ? pathname.startsWith(`/${locale}/service`)
      : href === `/${locale}/experiences`
        ? pathname.startsWith(`/${locale}/experiences`)
        : href === `/${locale}/lookup`
          ? pathname.startsWith(`/${locale}/lookup`)
          : false;

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled || menuOpen
            ? "border-b border-line bg-paper/90 backdrop-blur-md"
            : "bg-transparent"
        }`}
      >
        <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8">
          {/* Brand */}
          <Link
            href={`/${locale}`}
            className="group flex items-center"
            onClick={() => setMenuOpen(false)}
          >
            <BrandLogo className="h-10 w-[68px] transition-opacity group-hover:opacity-80" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-10 lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`text-[0.8125rem] tracking-[0.08em] transition-colors hover:text-accent-deep ${
                  isActive(item.href)
                    ? "text-strong"
                    : "text-body/80"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3 sm:gap-5">
            {/* Language switcher */}
            <div ref={langRef} className="relative">
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 rounded-full border border-line-strong bg-paper/70 px-3.5 py-1.5 text-xs tracking-[0.18em] text-muted uppercase backdrop-blur-sm transition-colors hover:border-strong hover:text-strong"
              >
                <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.1" />
                  <path d="M1.6 8h12.8M8 1.6c-4 3.6-4 9.2 0 12.8M8 1.6c4 3.6 4 9.2 0 12.8" stroke="currentColor" strokeWidth="1.1" />
                </svg>
                {locale}
              </button>
              {langOpen && (
                <ul
                  role="listbox"
                  className="absolute right-0 mt-2 w-44 overflow-hidden rounded-md border border-line bg-paper py-1 shadow-[0_20px_50px_-20px_rgba(35,33,29,0.25)]"
                >
                  {locales.map((l) => (
                    <li key={l} role="option" aria-selected={l === locale}>
                      <Link
                        href={localeHref(l)}
                        hrefLang={l}
                        scroll={false}
                        onClick={() => setLangOpen(false)}
                        className={`block px-4 py-2.5 text-sm tracking-wide transition-colors hover:bg-canvas ${
                          l === locale ? "text-accent-deep" : "text-body"
                        }`}
                      >
                        {localeNames[l]}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

             {/* CTA */}
             <Link
               href={`/${locale}/booking`}
               className="btn-ghost !hidden !px-6 !py-2.5 lg:!inline-flex"
             >
               {dict.nav.book}
             </Link>

            {/* Mobile burger */}
            <button
              type="button"
              aria-label="Menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="flex h-10 w-10 flex-col items-center justify-center gap-[7px]"
            >
              <span
                className={`h-px w-6 bg-strong transition-transform duration-300 ${
                  menuOpen ? "translate-y-[4px] rotate-45" : ""
                }`}
              />
              <span
                className={`h-px w-6 bg-strong transition-transform duration-300 ${
                  menuOpen ? "-translate-y-[4px] -rotate-45" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-40 bg-paper transition-opacity duration-300 lg:hidden ${
          menuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      >
        <nav className="flex h-full flex-col items-center justify-center gap-9 px-8">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setMenuOpen(false)}
              className="font-display text-3xl font-light tracking-wide text-strong transition-colors hover:text-accent-deep"
            >
              {item.label}
            </Link>
          ))}
          <OrnamentMark />
          <Link
             href={`/${locale}/booking`}
             onClick={() => setMenuOpen(false)}
             className="btn-dark"
           >
             {dict.nav.book}
           </Link>
        </nav>
      </div>
    </>
  );
}

function OrnamentMark() {
  return (
    <p className="font-display text-xs tracking-[0.35em] text-muted" aria-hidden>
      ( King Studio )
    </p>
  );
}
